import { createContext, useContext, useSyncExternalStore } from "react"

/**
 * The countdowns the server ticks — one value per clock, a fresh set every
 * second for as long as any of them is running.
 *
 * These deliberately live OUTSIDE the redux store. They used to arrive as
 * `myGame.timers`, which meant every tick handed redux a new `myGame` object,
 * and every component holding `state.global.myGame` (or `state.global` itself)
 * re-rendered once a second — the whole board, both sidebars, every player
 * card — to move one number on one ring. On a low-end device that alone ate the
 * frame budget.
 *
 * A tick is a pure display concern with no bearing on game state, so it gets
 * its own tiny store instead. Subscribing to a single key yields a plain
 * number, so React re-renders a subscriber only when that particular clock
 * actually changes, and nothing that doesn't ask for a countdown re-renders at
 * all.
 *
 * Read one with `useGameTimer`, or draw one with `Components/GameTimer` — the
 * ring subscribes for itself, so its parent need not.
 */
export type GameTimers = NonNullable<IGameDetails["timers"]>
export type GameTimerKey = keyof GameTimers

export interface GameTimersStore {
  getTimers: () => GameTimers
  setTimers: (next: GameTimers | undefined) => void
  subscribe: (listener: () => void) => () => void
}

const EMPTY_TIMERS: GameTimers = {}

const sameTimers = (a: GameTimers, b: GameTimers) => {
  const keys = Object.keys({ ...a, ...b }) as GameTimerKey[]
  return keys.every((key) => a[key] === b[key])
}

/**
 * A store of its own rather than a module-level singleton, because the guide
 * draws a second, frozen copy of the game screen against its own redux store
 * (Components/GuideGameMock.tsx) and its clocks must read the invented game's
 * numbers, not the live one's.
 */
export const createGameTimersStore = (
  initial: GameTimers = EMPTY_TIMERS
): GameTimersStore => {
  let timers: GameTimers = initial
  const listeners = new Set<() => void>()
  return {
    getTimers: () => timers,
    setTimers: (next) => {
      const value = next || EMPTY_TIMERS
      // Nothing moved — a broadcast that repeats the same numbers must not wake
      // the subscribers, or the saving above is given straight back.
      if (sameTimers(timers, value)) return
      timers = value
      listeners.forEach((listener) => listener())
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

/** The live game's clocks. Fed from the socket handlers in App.tsx. */
export const gameTimersStore = createGameTimersStore()

export const setGameTimers = (next: GameTimers | undefined) =>
  gameTimersStore.setTimers(next)

/** Leaving a game, or starting one: no clock is running until the server says so. */
export const resetGameTimers = () => gameTimersStore.setTimers(EMPTY_TIMERS)

export const GameTimersContext =
  createContext<GameTimersStore>(gameTimersStore)

export const useGameTimersStore = () => useContext(GameTimersContext)

/**
 * The seconds left on one clock. Returns a number (or undefined while that
 * clock isn't running), so a component subscribing here re-renders on that
 * clock's ticks and on nothing else.
 */
export const useGameTimer = (key: GameTimerKey): number | undefined => {
  const store = useGameTimersStore()
  return useSyncExternalStore(store.subscribe, () => store.getTimers()[key])
}

/**
 * Whether a clock is running at all, for the callers that only gate on its
 * presence. A boolean changes on the two edges rather than every second, so
 * this costs no per-tick renders.
 */
export const useHasGameTimer = (key: GameTimerKey): boolean => {
  const store = useGameTimersStore()
  return useSyncExternalStore(
    store.subscribe,
    () => store.getTimers()[key] !== undefined
  )
}
